import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react'
import Modal from 'react-modal'
import { googleMapsApiKey } from '../../config'

interface LocationViewProps {
  latitude: number
  longitude: number
  northeastLatitude?: number
  northeastLongitude?: number
  southwestLatitude?: number
  southwestLongitude?: number
  renderTrigger?: (openModal: () => void) => ReactNode
}

const MAP_SCRIPT_ID = 'google-maps-sdk'
let googleMapsPromise: Promise<void> | null = null

const loadGoogleMaps = (): Promise<void> => {
  if (window.google?.maps) {
    return Promise.resolve()
  }

  if (googleMapsPromise) {
    return googleMapsPromise
  }

  googleMapsPromise = new Promise<void>((resolve, reject) => {
    const handleLoad = () => resolve()
    const handleError = () => reject(new Error('Failed to load Google Maps script'))

    const existingScript = document.getElementById(MAP_SCRIPT_ID) as HTMLScriptElement | null

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true })
      existingScript.addEventListener('error', handleError, { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = MAP_SCRIPT_ID
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}`
    script.async = true
    script.defer = true
    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })
    document.head.appendChild(script)
  }).catch(error => {
    googleMapsPromise = null
    throw error
  })

  return googleMapsPromise
}

interface LocationMapProps extends LocationViewProps {
  className?: string
  style?: CSSProperties
  zoom?: number
}

export const LocationMap = ({
  latitude,
  longitude,
  northeastLatitude,
  northeastLongitude,
  southwestLatitude,
  southwestLongitude,
  className,
  style,
  zoom = 16,
}: LocationMapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let rectangle: any = null
    let marker: any = null
    let isCancelled = false

    const renderMap = () => {
      if (!containerRef.current || isCancelled || !window.google?.maps) {
        return
      }

      containerRef.current.innerHTML = ''

      const map = new window.google.maps.Map(containerRef.current, {
        center: { lat: latitude, lng: longitude },
        zoom,
        mapTypeId: 'roadmap',
        mapTypeControl: false,
        streetViewControl: false,
      })

      const hasBounds = northeastLatitude != null && northeastLongitude != null && southwestLatitude != null && southwestLongitude != null

      if (hasBounds) {
        const bounds = new window.google.maps.LatLngBounds(
          { lat: southwestLatitude as number, lng: southwestLongitude as number },
          { lat: northeastLatitude as number, lng: northeastLongitude as number }
        )

        map.fitBounds(bounds)

        rectangle = new window.google.maps.Rectangle({
          bounds,
          strokeColor: '#5A8A7C',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: '#5A8A7C',
          fillOpacity: 0.2,
          map,
        })
      } else {
        map.setCenter({ lat: latitude, lng: longitude })
        map.setZoom(zoom)
      }

      marker = new window.google.maps.Marker({
        position: { lat: latitude, lng: longitude },
        map,
        title: 'Location',
      })
    }

    loadGoogleMaps()
      .then(renderMap)
      .catch(error => {
        console.error(error)
      })

    return () => {
      isCancelled = true
      rectangle?.setMap(null)
      marker?.setMap(null)
    }
  }, [latitude, longitude, northeastLatitude, northeastLongitude, southwestLatitude, southwestLongitude, zoom])

  return <div ref={containerRef} className={className} style={style} />
}

const LocationView = ({
  latitude,
  longitude,
  northeastLatitude,
  northeastLongitude,
  southwestLatitude,
  southwestLongitude,
  renderTrigger,
}: LocationViewProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const openModal = () => setIsOpen(true)
  const closeModal = () => setIsOpen(false)

  return (
    <>
      {renderTrigger ? renderTrigger(openModal) : null}
      <Modal isOpen={isOpen} onRequestClose={closeModal} className="location-view-popup" overlayClassName="location-view-overlay">
        <div style={{ padding: '20px', width: '80vw', height: '80vh' }}>
          <LocationMap
            latitude={latitude}
            longitude={longitude}
            northeastLatitude={northeastLatitude}
            northeastLongitude={northeastLongitude}
            southwestLatitude={southwestLatitude}
            southwestLongitude={southwestLongitude}
            className="location-view-map"
            style={{ width: '100%', height: '100%', borderRadius: 4 }}
          />
        </div>
      </Modal>
    </>
  )
}

export default LocationView
